/**
 * @typedef {{ [key: string]: string }} MMYState
 */

(() => {
  const globalScope = /** @type {any} */ (window);
  if (globalScope.__componentSearchFormNativeLoaded) return;
  globalScope.__componentSearchFormNativeLoaded = true;
const MMY_STORAGE_KEY = "mmy";
const MMY_OPTIONS_CACHE_NAME = "mmy-options-v1";
const MMY_OPTIONS_CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 7;
const MMY_OPTIONS_CACHE_ORIGIN = "https://mmy-options-cache.invalid";
const MMY_OPTIONS_LEGACY_LOCAL_KEY = "mmy_options_cache_v2";

/** @type {Map<string, { text: string, expiresAt: number }>} */
const mmyOptionsMemoryCache = new Map();

/** @type {(keyof MMYState)[]} */
const MMY_SELECTION_KEYS = [
  "selmake",
  "selmodel",
  "selchassisgroup",
  "selchassislabel",
  "selheights",
];

/** Localhost test vehicle: add ?mmyExample=1 to the URL */
const MMY_DEV_EXAMPLE_SELECTIONS = {
  selmake: "TOYOTA",
  selmodel: "LAND CRUISER",
  selchassisgroup: "999800183",
  selchassislabel: "76 & 78 Series Troopy incl. LC70 98-26",
  selheights: "Standard Height",
};

/**
 * @param {MMYState} state
 * @returns {boolean}
 */
/**
 * @param {string} label
 * @returns {boolean}
 */
function isPlaceholderOptionLabel(label) {
  return /^choose\s+/i.test(String(label || "").trim());
}

/**
 * @param {MMYState} state
 * @returns {boolean}
 */
function hasMmyVehicleSelection(state) {
  return Boolean(String(state.selmake || "").trim());
}

/**
 * @param {MMYState} state
 * @returns {MMYState}
 */
function pickMmySelections(state) {
  /** @type {MMYState} */
  const selections = {};
  for (const key of MMY_SELECTION_KEYS) {
    selections[key] = String(state[key] || "").trim();
  }

  if (isPlaceholderOptionLabel(selections.selchassislabel)) {
    selections.selchassislabel = "";
  }
  if (isPlaceholderOptionLabel(selections.selmodel)) {
    selections.selmodel = "";
  }
  if (isPlaceholderOptionLabel(selections.selheights)) {
    selections.selheights = "";
  }
  if (!selections.selchassisgroup) {
    selections.selchassislabel = "";
  }

  return selections;
}

function applyMmyDevExampleIfRequested() {
  if (!isLocalThemePreview()) return;
  if (new URLSearchParams(window.location.search).get("mmyExample") !== "1") {
    return;
  }
  writeMMYState(MMY_DEV_EXAMPLE_SELECTIONS);
}
const MMY_DEBUG =
  new URLSearchParams(window.location.search).get("mmyDebug") === "1" ||
  sessionStorage.getItem("mmyDebug") === "1";

/**
 * @param {...any} args
 */
function mmyDebugLog(...args) {
  if (!MMY_DEBUG) return;
  console.log("[MMY]", ...args);
}

/**
 * @returns {boolean}
 */
function isLocalThemePreview() {
  return (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  );
}

const mmySearchState = /** @type {{ mmyFixturesUrl: string, mmyFixtures: { text: Record<string, string>, json: Record<string, unknown> } | null }} */ (
  globalScope.__mmySearchState ||
  (globalScope.__mmySearchState = {
    mmyFixturesUrl: "",
    mmyFixtures: null,
  })
);

/** @type {Promise<void> | null} */
let mmyBootstrapPromise = null;

/**
 * @returns {Promise<void>}
 */
function ensureMmyBootstrap() {
  if (!mmyBootstrapPromise) {
    mmyBootstrapPromise = (async () => {
      if (!globalScope.__mmyStorageMigrated) {
        migrateMmySessionToLocal();
        globalScope.__mmyStorageMigrated = true;
      }
      if (!globalScope.__mmyOptionsCacheMigrated) {
        await migrateMmyOptionsLocalStorageToCache();
        globalScope.__mmyOptionsCacheMigrated = true;
      }
    })();
  }
  return mmyBootstrapPromise;
}

/**
 * @returns {Promise<{ text: Record<string, string>, json: Record<string, unknown> } | null>}
 */
async function loadMMYFixtures() {
  if (!isLocalThemePreview() || !mmySearchState.mmyFixturesUrl) return null;
  if (mmySearchState.mmyFixtures) return mmySearchState.mmyFixtures;

  try {
    const response = await fetch(mmySearchState.mmyFixturesUrl, {
      method: "GET",
      credentials: "omit",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) {
      throw new TypeError(`HTTP ${response.status}`);
    }
    const doc = /** @type {{ text?: Record<string, string>, json?: Record<string, unknown> }} */ (
      await response.json()
    );
    mmySearchState.mmyFixtures = {
      text: doc.text || {},
      json: doc.json || {},
    };
    mmyDebugLog("loadMMYFixtures:ok", {
      url: mmySearchState.mmyFixturesUrl,
      textCount: Object.keys(mmySearchState.mmyFixtures.text).length,
      jsonCount: Object.keys(mmySearchState.mmyFixtures.json).length,
    });
    if (typeof console !== "undefined" && typeof console.info === "function") {
      console.info("[MMY] Using local fixtures from", mmySearchState.mmyFixturesUrl);
    }
    return mmySearchState.mmyFixtures;
  } catch (error) {
    mmyDebugLog("loadMMYFixtures:error", { url: mmySearchState.mmyFixturesUrl, error });
    return null;
  }
}

/**
 * @param {string} path
 * @param {Record<string, string>} params
 * @returns {Promise<string>}
 */
async function fetchMMYTextFromFixtures(path, params) {
  const fixtures = await loadMMYFixtures();
  if (!fixtures) return "";

  const key = getMMYCacheEntryKey(path, params);
  const text = fixtures.text[key];
  if (!text) {
    mmyDebugLog("fetchMMYText:fixture-miss", { key });
    if (typeof console !== "undefined" && typeof console.warn === "function") {
      console.warn(
        `[MMY] No fixture for "${key}". Run scripts/fetch-mmy-search-fixtures.sh to refresh.`,
      );
    }
    return "";
  }

  await setCachedMMYOptions(path, params, text);
  return text;
}

/**
 * @param {string} path
 * @param {Record<string, string>} params
 * @returns {Promise<unknown>}
 */
async function fetchMMYJsonFromFixtures(path, params) {
  const fixtures = await loadMMYFixtures();
  if (!fixtures) return null;

  const key = getMMYCacheEntryKey(path, params);
  const data = fixtures.json[key];
  if (data === undefined) {
    mmyDebugLog("fetchMMYJson:fixture-miss", { key });
    if (typeof console !== "undefined" && typeof console.warn === "function") {
      console.warn(
        `[MMY] No fixture for "${key}". Run scripts/fetch-mmy-search-fixtures.sh to refresh.`,
      );
    }
    return null;
  }

  return data;
}

/**
 * One-time migration from legacy sessionStorage MMY state.
 */
function migrateMmySessionToLocal() {
  try {
    const sessionRaw = sessionStorage.getItem(MMY_STORAGE_KEY);
    if (!sessionRaw) return;

    const sessionState = /** @type {MMYState} */ (
      JSON.parse(sessionRaw) || {}
    );
    const localState = readMMYStateFromLocalStorage();

    if (
      !hasMmyVehicleSelection(localState) &&
      hasMmyVehicleSelection(sessionState)
    ) {
      writeMMYState(sessionState);
      return;
    }

    sessionStorage.removeItem(MMY_STORAGE_KEY);
  } catch (error) {
    // Ignore migration errors.
  }
}

/**
 * @returns {MMYState}
 */
function readMMYStateFromLocalStorage() {
  try {
    return /** @type {MMYState} */ (
      JSON.parse(localStorage.getItem(MMY_STORAGE_KEY) || "{}") || {}
    );
  } catch (error) {
    return {};
  }
}

/**
 * Persist MMY state to localStorage and sync vehicle cookies for Liquid/SSR.
 * @param {MMYState} details
 * @param {{ silent?: boolean, source?: Element | null }} [options]
 */
function writeMMYState(details, options = {}) {
  /** @type {MMYState} */
  let next = pickMmySelections(details);

  try {
    const localState = pickMmySelections(readMMYStateFromLocalStorage());
    if (!hasMmyVehicleSelection(next)) {
      if (hasMmyVehicleSelection(localState)) return;
    }
  } catch (error) {
    // Ignore parse errors and continue with provided state.
  }

  if (!hasMmyVehicleSelection(next)) return;

  try {
    localStorage.setItem(MMY_STORAGE_KEY, JSON.stringify(next));
  } catch (error) {
    // Ignore storage errors (private mode, quota, etc).
    return;
  }

  if (hasMmyVehicleSelection(next)) {
    setMmyVehicleCookies(
      String(next.selmake || "").trim(),
      String(next.selmodel || "").trim(),
      String(next.selchassisgroup || "").trim(),
      String(next.selchassislabel || "").trim(),
      String(next.selheights || "").trim(),
    );
    if (String(next.selheights || "").trim()) {
      setMmyHeightCookie(String(next.selheights).trim());
    }
  }

  try {
    sessionStorage.removeItem(MMY_STORAGE_KEY);
  } catch (error) {
    // Ignore cleanup errors.
  }

  if (!options.silent) {
    document.dispatchEvent(
      new CustomEvent("mmy:selections-updated", {
        detail: { selections: next, source: options.source ?? null },
      }),
    );
  }
}

/**
 * @param {string} item
 * @param {string} value
 */
function setMMYStateKey(item, value) {
  try {
    const mmy = readMMYState();
    mmy[item] = value;
    writeMMYState(mmy);
  } catch (error) {
    return false;
  }
}

/**
 * @param {string} label
 * @returns {string}
 */
function heightLabelToSlug(label) {
  return label.trim().toLowerCase().replace(/\s+/g, "_").replace(/#/g, "");
}

/**
 * @param {string} name
 * @returns {string}
 */
function readMmyCookie(name) {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}=([^;]*)`),
  );
  const value = match?.[1];
  return value ? decodeURIComponent(value) : "";
}

/** @param {string} slug */
function setMmyHeightCookie(slug) {
  const normalized = heightLabelToSlug(slug);
  if (!normalized) return;
  document.cookie = `mmy_height=${encodeURIComponent(normalized)};path=/;max-age=604800;SameSite=Lax`;
}

/**
 * @param {HTMLSelectElement} chassisInput
 * @returns {string}
 */
function getChassisOptionLabel(chassisInput) {
  const value = chassisInput.value.trim();
  if (!value) return "";

  const option = chassisInput.selectedOptions[0];
  if (!option || !option.value.trim()) return "";

  const label = option.textContent.trim();
  return isPlaceholderOptionLabel(label) ? "" : label;
}

/**
 * @param {HTMLSelectElement} select
 * @param {string} value
 * @returns {boolean}
 */
function setSelectValue(select, value) {
  const normalized = String(value || "").trim();
  if (!normalized) return false;

  select.value = normalized;
  if (select.value === normalized) return true;

  for (const option of select.options) {
    if (option.value.trim() === normalized) {
      select.value = option.value;
      return true;
    }
  }

  return false;
}

/**
 * @param {HTMLSelectElement} select
 * @param {string} [groupId]
 * @param {string} [label]
 * @returns {boolean}
 */
function setChassisSelectValue(select, groupId, label) {
  if (groupId && setSelectValue(select, groupId)) return true;
  const normalizedLabel = String(label || "").trim();
  if (!normalizedLabel) return false;

  for (const option of select.options) {
    if (option.textContent.trim() === normalizedLabel) {
      select.value = option.value;
      return true;
    }
  }

  return false;
}

/**
 * @typedef {Pick<MMYState, "selmake" | "selmodel" | "selchassisgroup" | "selchassislabel" | "selheights">} MMYRestoreSelections
 */

/**
 * @param {string} nextKey
 * @param {MMYRestoreSelections} restoreSelections
 * @param {HTMLSelectElement} makeInput
 * @param {HTMLSelectElement} modelInput
 * @param {HTMLSelectElement} chassisInput
 * @param {HTMLSelectElement} heightInput
 */
function applyRestoreSelectionsForStep(
  nextKey,
  restoreSelections,
  makeInput,
  modelInput,
  chassisInput,
  heightInput,
) {
  if (nextKey === "get_makes" && restoreSelections.selmake) {
    setSelectValue(makeInput, restoreSelections.selmake);
  }
  if (nextKey === "get_models" && restoreSelections.selmodel) {
    setSelectValue(modelInput, restoreSelections.selmodel);
  }
  if (nextKey === "get_series") {
    setChassisSelectValue(
      chassisInput,
      restoreSelections.selchassisgroup,
      restoreSelections.selchassislabel,
    );
  }
  if (nextKey === "get_heights" && restoreSelections.selheights) {
    setSelectValue(heightInput, restoreSelections.selheights);
  }
}

/**
 * @param {string} make
 * @param {string} model
 * @param {string} chassisGroupId
 * @param {string} chassisLabel
 * @param {string} height
 */
function setMmyVehicleCookies(make, model, chassisGroupId, chassisLabel, height) {
  const maxAge = "max-age=604800;path=/;SameSite=Lax";
  if (make) {
    document.cookie = `mmy_make=${encodeURIComponent(make)};${maxAge}`;
  }
  if (model) {
    document.cookie = `mmy_model=${encodeURIComponent(model)};${maxAge}`;
  }
  if (chassisLabel) {
    document.cookie = `mmy_chassis=${encodeURIComponent(chassisLabel)};${maxAge}`;
  }
  if (chassisGroupId) {
    document.cookie = `mmy_cg=${encodeURIComponent(chassisGroupId)};${maxAge}`;
  }
  if (height) {
    setMmyHeightCookie(height);
  }
}

/** @param {string} collectionPath */
function setMmyCollectionCookie(collectionPath) {
  if (!collectionPath) return;
  document.cookie = `mmy_collection=${encodeURIComponent(collectionPath)};path=/;max-age=604800;SameSite=Lax`;
}

/**
 * @param {HTMLSelectElement} select
 * @param {string} heightSlug
 * @returns {string}
 */
function heightLabelFromCookieSlug(select, heightSlug) {
  const normalizedSlug = heightLabelToSlug(heightSlug);
  if (!normalizedSlug) return "";

  for (const option of select.options) {
    const value = option.value.trim();
    if (!value) continue;
    if (heightLabelToSlug(value) === normalizedSlug) return value;
  }

  return "";
}

/**
 * A hung host must not stall the whole chain: each base gets this long before the
 * next one is tried (the Worker proxy answers from its edge cache well inside it).
 */
const MMY_FETCH_TIMEOUT_MS = 4000;

/** @param {number} ms */
function mmyTimeoutSignal(ms) {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

/**
 * @param {string} path
 * @returns {string[]}
 */
function mmyUrls(path) {
  const globalConfig = /** @type {{ MMY_BASE_URL?: string }} */ (window);
  const configuredBase = globalConfig.MMY_BASE_URL || "";
  const sameOriginBase = `${window.location.origin}/shopify/index.php/shopify/mmy_search`;
  const legacyBase = "https://shopify.airbagman.com.au/shopify/index.php/shopify/mmy_search";

  // The legacy endpoint's CORS allowlist only covers the production storefront origin;
  // the Worker proxy serves preview/dev domains with open CORS.
  const workerProxyBase =
    "https://airbagman-netsuite-quote-production.airbagman-netsuite-worker.workers.dev/mmy";

  /** @type {string[]} */
  const bases = [];
  if (configuredBase) bases.push(configuredBase.replace(/\/$/, ""));
  // Worker proxy first: edge-cached, open CORS, and it can't hang the way the legacy
  // PHP host can. Same-origin is only real on the legacy host itself.
  bases.push(workerProxyBase, legacyBase, sameOriginBase);

  const uniqueBases = Array.from(new Set(bases));
  const rand = Math.floor(Date.now() / 1000);
  return uniqueBases.map((base) => `${base}/${path}/?rand=${rand}`);
}

/**
 * @param {string} path
 * @param {Record<string, string>} params
 * @returns {string}
 */
function getMMYCacheEntryKey(path, params) {
  const sortedPairs = Object.entries(params)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${value || ""}`);
  return `${path}|${sortedPairs.join("&")}`;
}

/**
 * @returns {boolean}
 */
function isMmyOptionsCacheStorageAvailable() {
  return typeof caches !== "undefined";
}

/**
 * @param {string} cacheKey
 * @returns {Request}
 */
function getMmyOptionsCacheRequest(cacheKey) {
  return new Request(
    `${MMY_OPTIONS_CACHE_ORIGIN}/${encodeURIComponent(cacheKey)}`,
  );
}

/**
 * @returns {Promise<Cache | null>}
 */
async function openMmyOptionsCache() {
  if (!isMmyOptionsCacheStorageAvailable()) return null;

  try {
    return await caches.open(MMY_OPTIONS_CACHE_NAME);
  } catch (error) {
    mmyDebugLog("openMmyOptionsCache:error", error);
    return null;
  }
}

/**
 * @param {string} cacheKey
 * @returns {Promise<{ text: string, expiresAt: number } | null>}
 */
async function readMmyOptionsCacheEntry(cacheKey) {
  const memoryEntry = mmyOptionsMemoryCache.get(cacheKey);
  if (memoryEntry) {
    if (memoryEntry.expiresAt > Date.now()) return memoryEntry;
    mmyOptionsMemoryCache.delete(cacheKey);
  }

  const cache = await openMmyOptionsCache();
  if (!cache) return null;

  const request = getMmyOptionsCacheRequest(cacheKey);
  const response = await cache.match(request);
  if (!response) return null;

  const expiresAt = Number(response.headers.get("X-MMY-Expires-At") || 0);
  if (!expiresAt || expiresAt <= Date.now()) {
    await cache.delete(request);
    return null;
  }

  const text = await response.text();
  const entry = { text, expiresAt };
  mmyOptionsMemoryCache.set(cacheKey, entry);
  return entry;
}

/**
 * @param {string} cacheKey
 * @param {string} text
 * @param {number} expiresAt
 */
async function writeMmyOptionsCacheEntry(cacheKey, text, expiresAt) {
  mmyOptionsMemoryCache.set(cacheKey, { text, expiresAt });

  const cache = await openMmyOptionsCache();
  if (!cache) return;

  const request = getMmyOptionsCacheRequest(cacheKey);
  const response = new Response(text, {
    headers: {
      "Content-Type": "text/html; charset=utf-8",
      "X-MMY-Expires-At": String(expiresAt),
    },
  });

  try {
    await cache.put(request, response);
  } catch (error) {
    mmyDebugLog("writeMmyOptionsCacheEntry:error", { cacheKey, error });
  }
}

async function migrateMmyOptionsLocalStorageToCache() {
  try {
    const raw = localStorage.getItem(MMY_OPTIONS_LEGACY_LOCAL_KEY);
    if (!raw) return;

    const legacy = /** @type {Record<string, { text: string, expiresAt: number }>} */ (
      JSON.parse(raw) || {}
    );
    const now = Date.now();

    for (const [cacheKey, entry] of Object.entries(legacy)) {
      if (!entry?.text || !entry.expiresAt || entry.expiresAt <= now) continue;
      await writeMmyOptionsCacheEntry(cacheKey, entry.text, entry.expiresAt);
    }

    localStorage.removeItem(MMY_OPTIONS_LEGACY_LOCAL_KEY);
    mmyDebugLog("migrateMmyOptionsLocalStorageToCache:done");
  } catch (error) {
    mmyDebugLog("migrateMmyOptionsLocalStorageToCache:error", error);
  }
}

/**
 * @param {string} path
 * @param {Record<string, string>} params
 * @returns {Promise<string>}
 */
async function getCachedMMYOptions(path, params) {
  const cacheKey = getMMYCacheEntryKey(path, params);
  const entry = await readMmyOptionsCacheEntry(cacheKey);
  return entry?.text || "";
}

/**
 * @param {string} path
 * @param {Record<string, string>} params
 * @param {string} text
 */
async function setCachedMMYOptions(path, params, text) {
  const cacheKey = getMMYCacheEntryKey(path, params);
  await writeMmyOptionsCacheEntry(
    cacheKey,
    text,
    Date.now() + MMY_OPTIONS_CACHE_TTL_MS,
  );
}

/**
 * @param {string} path
 * @param {Record<string, string>} params
 * @returns {Promise<{ response: Response, text: string, url: string }>}
 */
async function fetchMMYText(path, params = {}) {
  const cachedText = await getCachedMMYOptions(path, params);
  if (cachedText) {
    mmyDebugLog("fetchMMYText:cache-hit", { path, params });
    return {
      response: new Response(cachedText, { status: 200 }),
      text: cachedText,
      url: `cache:${path}`,
    };
  }

  if (isLocalThemePreview()) {
    const fixtureText = await fetchMMYTextFromFixtures(path, params);
    if (fixtureText) {
      return {
        response: new Response(fixtureText, { status: 200 }),
        text: fixtureText,
        url: `fixture:${path}`,
      };
    }

    const fixtureKey = getMMYCacheEntryKey(path, params);
    throw new TypeError(
      `No MMY fixture for "${fixtureKey}". Run scripts/fetch-mmy-search-fixtures.sh`,
    );
  }

  const urls = mmyUrls(path);

  for (const candidate of urls) {
    const url = new URL(candidate);
    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(key, value || "");
    }

    try {
      mmyDebugLog("fetchMMYText:try", { path, url: url.toString() });
      const response = await fetch(url.toString(), {
        signal: mmyTimeoutSignal(MMY_FETCH_TIMEOUT_MS),
      });
      const text = await response.text();
      if (!response.ok) {
        mmyDebugLog("fetchMMYText:non-ok", {
          path,
          url: url.toString(),
          status: response.status,
        });
        continue;
      }
      await setCachedMMYOptions(path, params, text);
      return { response, text, url: url.toString() };
    } catch (error) {
      mmyDebugLog("fetchMMYText:error", { path, url: url.toString(), error });
    }
  }

  throw new TypeError(`MMY text request failed for step: ${path}`);
}

/**
 * @param {string} path
 * @param {Record<string, string>} params
 * @returns {Promise<{ response: Response, data: any, url: string }>}
 */
async function fetchMMYJson(path, params = {}) {
  if (isLocalThemePreview()) {
    const fixtureData = await fetchMMYJsonFromFixtures(path, params);
    if (fixtureData !== null) {
      return {
        response: new Response(JSON.stringify(fixtureData), { status: 200 }),
        data: fixtureData,
        url: `fixture:${path}`,
      };
    }

    const fixtureKey = getMMYCacheEntryKey(path, params);
    throw new TypeError(
      `No MMY fixture for "${fixtureKey}". Run scripts/fetch-mmy-search-fixtures.sh`,
    );
  }

  const urls = mmyUrls(path);

  for (const candidate of urls) {
    const url = new URL(candidate);
    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(key, value || "");
    }

    try {
      mmyDebugLog("fetchMMYJson:try", { path, url: url.toString() });
      const response = await fetch(url.toString());
      const data = await response.json();
      if (!response.ok) {
        mmyDebugLog("fetchMMYJson:non-ok", {
          path,
          url: url.toString(),
          status: response.status,
        });
        continue;
      }
      return { response, data, url: url.toString() };
    } catch (error) {
      mmyDebugLog("fetchMMYJson:error", { path, url: url.toString(), error });
    }
  }

  throw new TypeError(`MMY json request failed for step: ${path}`);
}

/**
 * @param {string} param
 * @returns {string | null}
 */
function getQueryParam(param) {
  return new URLSearchParams(window.location.search).get(param);
}

function initTabs() {
  const wrappers = Array.from(document.querySelectorAll(".search-form"));
  mmyDebugLog("initTabs", { wrapperCount: wrappers.length });

  wrappers.forEach((wrapper) => {
    if (wrapper.getAttribute("data-tabs-initialized") === "true") return;
    wrapper.setAttribute("data-tabs-initialized", "true");

    const tabItems = Array.from(
      wrapper.querySelectorAll(".search-tabs .tab-item"),
    );
    const tabLinks = Array.from(
      wrapper.querySelectorAll(".search-tabs .tab-item button[data-tab]"),
    );
    const tabPanels = Array.from(wrapper.querySelectorAll(".search-tab"));
    if (!tabLinks.length || !tabPanels.length) return;

    /**
     * @param {string} tabName
     */
    const activateTab = (tabName) => {
      tabItems.forEach((item) => {
        const link = item.querySelector("button[data-tab]");
        const active = !!link && link.getAttribute("data-tab") === tabName;
        item.classList.toggle("active", active);
        item.classList.toggle("initial-active", active);
        item.classList.toggle("inactive", !active);
      });

      tabPanels.forEach((panel) => {
        const active = panel.classList.contains(tabName);
        panel.classList.toggle("active", active);
        panel.classList.toggle("initial-active", active);
        panel.classList.toggle("inactive", !active);
      });
    };

    const current = tabLinks.find(
      (link) =>
        link.parentElement?.classList.contains("active") ||
        link.parentElement?.classList.contains("initial-active"),
    );
    const initialTab = (current || tabLinks[0])?.getAttribute("data-tab");
    if (initialTab) activateTab(initialTab);

    tabLinks.forEach((link) => {
      link.addEventListener("click", (event) => {
        event.preventDefault();
        const tabName = link.getAttribute("data-tab");
        if (!tabName) return;
        activateTab(tabName);
      });
    });
  });
}

function loadProductQuery() {
  const query = getQueryParam("product");
  if (!query || typeof globalScope.loadProductResource !== "function") return;

  setTimeout(() => {
    const input = document.querySelector(
      ".tab-part-number .search__input.field__input",
    );
    if (input instanceof HTMLInputElement) input.value = query;
    const link = document.querySelector(
      '.search-tabs .tab-item button[data-tab="tab-part-number"]',
    );
    if (link instanceof HTMLElement) link.click();
    globalScope.loadProductResource(query, "", "");
  });
}

/**
 * @returns {MMYState}
 */
function readMMYState() {
  return pickMmySelections(readMMYStateFromLocalStorage());
}

/**
 * @param {Element} formElement
 * @param {string} field
 * @returns {HTMLSelectElement | null}
 */
function getMmyFieldSelect(formElement, field) {
  const select = formElement.querySelector(`[data-mmy-field="${field}"]`);
  return select instanceof HTMLSelectElement ? select : null;
}

/**
 * @param {Element | null} formElement
 */
async function initVehicleKitsSearch(formElement) {
  if (!(formElement instanceof Element)) return;
  if (formElement.getAttribute("data-mmy-initialized") === "true") return;

  const fixturesFromForm = formElement.getAttribute("data-mmy-fixtures-url");
  if (fixturesFromForm) mmySearchState.mmyFixturesUrl = fixturesFromForm;

  await ensureMmyBootstrap();

  const makeInput = getMmyFieldSelect(formElement, "make");
  const modelInput = getMmyFieldSelect(formElement, "model");
  const chassisInput = getMmyFieldSelect(formElement, "chassis");
  const heightInput = getMmyFieldSelect(formElement, "height");
  const submitButton = formElement.querySelector(".mmy-search__submit");
  if (
    !(makeInput instanceof HTMLSelectElement) ||
    !(modelInput instanceof HTMLSelectElement) ||
    !(chassisInput instanceof HTMLSelectElement) ||
    !(heightInput instanceof HTMLSelectElement) ||
    !(submitButton instanceof HTMLButtonElement)
  ) {
    mmyDebugLog("initVehicleKitsSearch:missing-inputs", {
      makeInput: makeInput instanceof HTMLSelectElement,
      modelInput: modelInput instanceof HTMLSelectElement,
      chassisInput: chassisInput instanceof HTMLSelectElement,
      heightInput: heightInput instanceof HTMLSelectElement,
      submitButton: submitButton instanceof HTMLButtonElement,
    });
    return;
  }

  formElement.setAttribute("data-mmy-initialized", "true");
  mmyDebugLog("initVehicleKitsSearch:start", formElement);

  /** @type {MMYState} */
  const defaultMMY = {
    make: '<option value="">Choose Make</option>',
    selmake: "",
    model: '<option value="">Choose Model</option>',
    selmodel: "",
    chassisgroup: '<option value="">Choose Chassis</option>',
    selchassisgroup: "",
    selchassislabel: "",
    heights: '<option value="">Choose Height</option>',
    selheights: "",
  };

  /**
   * @param {HTMLSelectElement} select
   * @param {string | undefined} html
   */
  const setHtml = (select, html) => {
    select.innerHTML = html || "";
  };

  /**
   * @param {HTMLSelectElement} select
   * @param {string} html
   * @param {string} [value]
   */
  const resetSelect = (select, html, value = "") => {
    setHtml(select, html);
    select.value = value;
  };

  /**
   * @param {MMYState} state
   * @param {"get_makes" | "get_models" | "get_series"} step
   * @returns {MMYState}
   */
  const clearDependentMmyOptions = (state, step) => {
    const next = { ...state };

    if (step === "get_makes") {
      next.selmodel = "";
      next.selchassisgroup = "";
      next.selchassislabel = "";
      next.selheights = "";
      return next;
    }

    if (step === "get_models") {
      next.selchassisgroup = "";
      next.selchassislabel = "";
      next.selheights = "";
      return next;
    }

    if (step === "get_series") {
      next.selheights = "";
    }

    return next;
  };

  const readCurrentSelections = () => {
    const selmake = makeInput.value.trim();
    if (!selmake) return null;

    return pickMmySelections({
      selmake,
      selmodel: modelInput.value.trim(),
      selchassisgroup: chassisInput.value.trim(),
      selchassislabel: getChassisOptionLabel(chassisInput),
      selheights: heightInput.value.trim(),
    });
  };

  /**
   * @param {MMYState} selections
   * @param {{ silent?: boolean }} [options]
   */
  const persistSelections = (selections, options = {}) => {
    if (!selections || !hasMmyVehicleSelection(selections)) return;
    writeMMYState(selections, { silent: options.silent, source: formElement });
  };

  const updateSubmitButton = () => {
    if (submitButton.classList.contains("button-loading")) return;

    submitButton.disabled = !(
      makeInput.value &&
      modelInput.value &&
      chassisInput.value &&
      heightInput.value
    );
  };

  /**
   * @param {string} step
   * @param {HTMLSelectElement} input
   * @param {{ persist?: boolean, restoreSelections?: MMYRestoreSelections }} [options]
   */
  const onChangeMMY = async (step, input, options = {}) => {
    const persist = options.persist !== false;
    const restoreSelections = options.restoreSelections;
    const isRestore = Boolean(restoreSelections);
    mmyDebugLog("onChangeMMY:start", {
      step,
      value: input.value,
      make: makeInput.value,
      model: modelInput.value,
      chassis: chassisInput.value,
      height: heightInput.value,
    });

    if (typeof globalScope.resetResourcesPage === "function")
      globalScope.resetResourcesPage(false);
    input.value = input.value.trim();

    if (step === "form_load") {
      resetSelect(makeInput, defaultMMY.make);
      resetSelect(modelInput, defaultMMY.model);
      resetSelect(chassisInput, defaultMMY.chassisgroup);
      resetSelect(heightInput, defaultMMY.heights);
    } else if (step === "get_makes") {
      resetSelect(modelInput, defaultMMY.model);
      resetSelect(chassisInput, defaultMMY.chassisgroup);
      resetSelect(heightInput, defaultMMY.heights);
    } else if (step === "get_models") {
      resetSelect(chassisInput, defaultMMY.chassisgroup);
      resetSelect(heightInput, defaultMMY.heights);
    } else if (step === "get_series") {
      resetSelect(heightInput, defaultMMY.heights);
    }

    const mmy = readMMYState();
    /** @type {HTMLSelectElement | null} */
    let nextInput = null;
    /** @type {MMYState} */
    let params = {};
    let nextKey = step;

    if (step === "form_load") {
      nextKey = "get_makes";
      nextInput = makeInput;
      // Always fetch the full makes list (fixture key uses empty make_sel).
      params = { make_sel: "" };
    } else if (step === "get_makes") {
      nextKey = "get_models";
      nextInput = modelInput;
      params = { make: input.value };
    } else if (step === "get_models") {
      nextKey = "get_series";
      nextInput = chassisInput;
      params = { make: makeInput.value, model: input.value };
    } else if (step === "get_series") {
      nextKey = "get_heights";
      nextInput = heightInput;
      params = {
        make: makeInput.value,
        model: modelInput.value,
        cg: input.value,
      };
    }

    /** @type {MMYState} */
    let sessionChanges = { ...mmy };
    if (isRestore && restoreSelections) {
      sessionChanges.selmake = restoreSelections.selmake || "";
      sessionChanges.selmodel = restoreSelections.selmodel || "";
      sessionChanges.selchassisgroup = restoreSelections.selchassisgroup || "";
      sessionChanges.selheights = restoreSelections.selheights || "";
      sessionChanges.selchassislabel = mmy.selchassislabel || "";
    } else {
      sessionChanges.selmake = makeInput.value.trim();
      sessionChanges.selmodel = modelInput.value.trim();
      sessionChanges.selchassisgroup = chassisInput.value.trim();
      sessionChanges.selchassislabel = getChassisOptionLabel(chassisInput);
      sessionChanges.selheights = heightInput.value.trim();
    }

    if (!isRestore) {
      if (step === "get_makes") {
        sessionChanges = clearDependentMmyOptions(sessionChanges, "get_makes");
      } else if (step === "get_models") {
        sessionChanges = clearDependentMmyOptions(sessionChanges, "get_models");
      } else if (step === "get_series") {
        sessionChanges = clearDependentMmyOptions(sessionChanges, "get_series");
      }
    }

    if (!nextInput) {
      if (persist && step !== "form_load") persistSelections(readCurrentSelections());
      updateSubmitButton();
      return;
    }

    const requestParams = /** @type {Record<string, string>} */ (params);
    const cachedHtml = await getCachedMMYOptions(nextKey, requestParams);
    if (cachedHtml) {
      setHtml(nextInput, cachedHtml);
      if (restoreSelections) {
        applyRestoreSelectionsForStep(
          nextKey,
          restoreSelections,
          makeInput,
          modelInput,
          chassisInput,
          heightInput,
        );
      }
      if (persist && step !== "form_load") persistSelections(readCurrentSelections());
      mmyDebugLog("onChangeMMY:options-cache-hit", {
        step: nextKey,
        params: requestParams,
        value: input.value,
      });
      updateSubmitButton();
      return;
    }

    const loadingOption = nextInput.querySelector('option[value=""]');
    if (loadingOption && step !== "form_load")
      loadingOption.textContent = "Loading...";

    let html = "";
    try {
      const result = await fetchMMYText(
        nextKey,
        /** @type {Record<string, string>} */ (params),
      );
      html = result.text;
      mmyDebugLog("onChangeMMY:response", {
        step: nextKey,
        url: result.url,
        ok: result.response.ok,
        status: result.response.status,
        htmlLength: html.length,
      });
      setHtml(nextInput, html);
      if (restoreSelections) {
        applyRestoreSelectionsForStep(
          nextKey,
          restoreSelections,
          makeInput,
          modelInput,
          chassisInput,
          heightInput,
        );
      }
    } catch (error) {
      console.error("[MMY] Failed loading options", { step: nextKey, error });
      if (isLocalThemePreview() && nextInput) {
        const message =
          error instanceof Error && error.message.includes("No MMY fixture")
            ? error.message
            : "MMY fixture missing for this selection. Run scripts/fetch-mmy-search-fixtures.sh";
        setHtml(
          nextInput,
          `<option value="">${message.replace(/</g, "&lt;")}</option>`,
        );
      } else if (step === "form_load" && isLocalThemePreview()) {
        setHtml(
          makeInput,
          '<option value="">MMY fixtures missing. Run scripts/fetch-mmy-search-fixtures.sh</option>',
        );
      }
      if (persist && step !== "form_load") persistSelections(readCurrentSelections());
      updateSubmitButton();
      return;
    }

    if (persist && step !== "form_load") persistSelections(readCurrentSelections());
    updateSubmitButton();
  };

  const restoreSavedSelections = async () => {
    const savedState = readMMYState();
    if (!savedState.selmake) {
      updateSubmitButton();
      return;
    }

    /** @type {MMYRestoreSelections} */
    const restoreSelections = {
      selmake: String(savedState.selmake || "").trim(),
      selmodel: String(savedState.selmodel || "").trim(),
      selchassisgroup: String(savedState.selchassisgroup || "").trim(),
      selchassislabel: String(savedState.selchassislabel || "").trim(),
      selheights: String(savedState.selheights || "").trim(),
    };

    setSelectValue(makeInput, restoreSelections.selmake);
    await onChangeMMY("get_makes", makeInput, {
      persist: false,
      restoreSelections,
    });

    if (!restoreSelections.selmodel) {
      updateSubmitButton();
      return;
    }

    await onChangeMMY("get_models", modelInput, {
      persist: false,
      restoreSelections,
    });

    if (!restoreSelections.selchassisgroup) {
      updateSubmitButton();
      return;
    }

    await onChangeMMY("get_series", chassisInput, {
      persist: false,
      restoreSelections,
    });

    if (!restoreSelections.selheights) {
      const cookieHeight = readMmyCookie("mmy_height");
      const heightLabel = heightLabelFromCookieSlug(heightInput, cookieHeight);
      if (heightLabel) {
        restoreSelections.selheights = heightLabel;
      }
    }

    if (restoreSelections.selheights) {
      setSelectValue(heightInput, restoreSelections.selheights);
    }

    const chassisLabel =
      getChassisOptionLabel(chassisInput) || restoreSelections.selchassislabel;

    persistSelections(
      pickMmySelections({
        selmake: makeInput.value.trim() || restoreSelections.selmake,
        selmodel: modelInput.value.trim() || restoreSelections.selmodel,
        selchassisgroup:
          chassisInput.value.trim() || restoreSelections.selchassisgroup,
        selchassislabel: chassisLabel,
        selheights: heightInput.value.trim() || restoreSelections.selheights,
      }),
      { silent: true },
    );
    updateSubmitButton();
  };

  const hydrateSelections = async () => {
    if (formElement.getAttribute("data-mmy-hydrating") === "true") return;
    formElement.setAttribute("data-mmy-hydrating", "true");
    try {
      applyMmyDevExampleIfRequested();
      resetSelect(makeInput, defaultMMY.make);
      resetSelect(modelInput, defaultMMY.model);
      resetSelect(chassisInput, defaultMMY.chassisgroup);
      resetSelect(heightInput, defaultMMY.heights);
      await onChangeMMY("form_load", makeInput, { persist: false });
      await restoreSavedSelections();
    } finally {
      formElement.removeAttribute("data-mmy-hydrating");
    }
  };

  document.addEventListener("mmy:selections-updated", (event) => {
    if (!(event instanceof CustomEvent)) return;
    if (event.detail?.source === formElement) return;
    hydrateSelections();
  });

  hydrateSelections();
  makeInput.addEventListener("change", () =>
    onChangeMMY("get_makes", makeInput),
  );
  modelInput.addEventListener("change", () =>
    onChangeMMY("get_models", modelInput),
  );
  chassisInput.addEventListener("change", () =>
    onChangeMMY("get_series", chassisInput),
  );
  heightInput.addEventListener("change", () => {
    const selected = heightInput.value.trim();
    if (!selected) {
      onChangeMMY("get_series", chassisInput);
      return;
    }
    const mmy = readMMYState();
    mmy.selheights = selected;
    writeMMYState(pickMmySelections(mmy), { source: formElement });
    document.dispatchEvent(new CustomEvent("mmy:apply-filter"));
    updateSubmitButton();
  });

  submitButton.addEventListener("click", async (event) => {
    event.preventDefault();
    if (
      !makeInput.value ||
      !modelInput.value ||
      !chassisInput.value ||
      !heightInput.value
    )
      return;
    setSearchButtonLoading(submitButton, true);
    submitButton.disabled = true;

    /** @type {{ collection_handle: string }} */
    let json;
    try {
      const result = await fetchMMYJson("get_results", {
        make: makeInput.value.trim(),
        model: modelInput.value.trim(),
        cg: chassisInput.value.trim(),
        hg: heightInput.value.trim(),
      });
      json = result.data;
      mmyDebugLog("submit:response", {
        url: result.url,
        ok: result.response.ok,
        status: result.response.status,
        collection_handle: json?.collection_handle,
      });
    } catch (error) {
      setSearchButtonLoading(submitButton, false);
      updateSubmitButton();
      console.error("[MMY] Failed to resolve search results", error);
      return;
    }

    const origin = window.location.origin;
    const collectionUrl = new URL(json.collection_handle, origin);

    const selections = readCurrentSelections();
    if (selections) writeMMYState(selections, { source: formElement });
    setMmyCollectionCookie(collectionUrl.pathname);
    document.dispatchEvent(new CustomEvent('mmy:apply-filter'));

    if (window.location.pathname.indexOf(chassisInput.value) !== -1) {
      window.location.reload();
    } else {
      window.location.href = collectionUrl.toString();
    }
  });

  loadProductQuery();
}

/**
 * @param {ParentNode | Document} [root]
 */
function resetMmySearchInitialization(root = document) {
  const scopes = Array.from(root.querySelectorAll(".mmy-search"));
  for (const scope of scopes) {
    scope.removeAttribute("data-mmy-initialized");
    scope.removeAttribute("data-mmy-hydrating");
  }
  const wrappers = Array.from(root.querySelectorAll(".search-form"));
  for (const wrapper of wrappers) {
    wrapper.removeAttribute("data-tabs-initialized");
  }
}

/**
 * @param {HTMLButtonElement} button
 * @param {boolean} loading
 */
function setSearchButtonLoading(button, loading) {
  button.classList.toggle("button-loading", loading);
  button.toggleAttribute("aria-busy", loading);
}

/**
 * @param {ParentNode | Document} [root]
 */
function initPartNumberSearch(root = document) {
  const forms = Array.from(
    root.querySelectorAll(".tab-part-number form.search, .tab-site-search form.search"),
  );

  for (const form of forms) {
    if (!(form instanceof HTMLFormElement)) continue;
    if (form.getAttribute("data-part-search-initialized") === "true") continue;
    form.setAttribute("data-part-search-initialized", "true");

    form.addEventListener("submit", () => {
      const submitButton = form.querySelector(".gobutton.field__button");
      if (!(submitButton instanceof HTMLButtonElement)) return;

      setSearchButtonLoading(submitButton, true);
      submitButton.disabled = true;
    });
  }
}

/**
 * @param {ParentNode | Document} [root]
 */
function initAllVehicleKitsSearch(root = document) {
  const scopes = Array.from(root.querySelectorAll(".mmy-search"));
  mmyDebugLog("initAllVehicleKitsSearch", { scopeCount: scopes.length, root });
  for (const scope of scopes) {
    initVehicleKitsSearch(scope);
  }
}

/**
 * @param {ParentNode | Document} [root]
 */
function initializeSearchForms(root = document) {
  initTabs();
  initAllVehicleKitsSearch(root);
  initPartNumberSearch(root);
}

/** @type {number | null} */
let mmyReinitFrame = null;

/**
 * Header section hydration morphs menu markup after first init, which clears
 * selects while leaving data-mmy-initialized set. Re-init when that completes.
 * @param {ParentNode | Document} [root]
 */
function reinitializeSearchFormsAfterDomRefresh(root = document) {
  resetMmySearchInitialization(root);
  initializeSearchForms(root);
}

function scheduleSearchFormsReinit(root = document) {
  if (mmyReinitFrame !== null) {
    cancelAnimationFrame(mmyReinitFrame);
  }
  mmyReinitFrame = requestAnimationFrame(() => {
    mmyReinitFrame = null;
    reinitializeSearchFormsAfterDomRefresh(root);
  });
}

function watchSearchFormsForDomRefresh() {
  document.addEventListener("shopify:section:hydrated", () => {
    scheduleSearchFormsReinit(document);
  });

  const headerSection = document.querySelector(".header-section");
  if (headerSection) {
    const hydrationObserver = new MutationObserver(() => {
      if (headerSection.dataset.hydrated !== "true") return;
      scheduleSearchFormsReinit(document);
    });
    hydrationObserver.observe(headerSection, {
      attributes: true,
      attributeFilter: ["data-hydrated"],
    });
  }

  const domObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (
          node.matches(".mmy-search") ||
          node.querySelector(".mmy-search:not([data-mmy-initialized])")
        ) {
          initializeSearchForms(node);
          return;
        }
      }
    }
  });
  domObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

  globalScope.initTabs = initTabs;
  globalScope.initVehicleKitsSearch = initVehicleKitsSearch;
  globalScope.initAllVehicleKitsSearch = initAllVehicleKitsSearch;
  globalScope.initializeSearchForms = initializeSearchForms;
  globalScope.reinitializeSearchFormsAfterDomRefresh =
    reinitializeSearchFormsAfterDomRefresh;

  watchSearchFormsForDomRefresh();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initializeSearchForms(document);
    });
  } else {
    initializeSearchForms(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (!(event.target instanceof Element)) return;
    initializeSearchForms(event.target);
  });
})();
